import React from "react";
import "./BotAdditionalSetting.css"

const BotAdditionalSetting = () => {
  return (
    <div className="docs-bot-settings-container">
      <h1 className="docs-bot-settings-title">Configuring Bot Settings</h1>

      <p className="docs-bot-settings-intro">
        Customize and fine-tune the message settings, usage, security, and support options for your chatbot to ensure optimal performance and user experience.
      </p>

      <h2 className="docs-bot-settings-section-title">Messaging</h2>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">AI Model</h3>
        <p>
          Select the AI model for your bot. Free tier users are limited to <strong>GPT-4o mini</strong>. 
          Upgrading to Starter, Professional, or Advanced plans unlocks additional AI model options.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Conversational Mode</h3>
        <p>
          When enabled, the bot segments its responses into shorter, more readable messages for better user experience.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Strict FAQ Responses</h3>
        <p>
          Limit responses strictly to the content available in your FAQ. Ideal for scenarios where precise answers are critical.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Response Length</h3>
        <p>
          Adjust the verbosity of your bot’s responses. Shorter responses are concise, while longer responses provide more detailed explanations.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Suggest Follow Up Questions</h3>
        <p>
          When enabled, the bot will suggest two relevant follow-up questions from your knowledge base to encourage continued engagement.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Allow Image Usage</h3>
        <p>
          Allows the bot to incorporate scraped images for training and response generation.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Show Sources with Response</h3>
        <p>
          Toggle visibility of the sources along with the bot’s responses to maintain transparency.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Post Chat Feedback</h3>
        <p>
          Enable or disable the feedback prompt after a conversation to collect user insights.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Multilingual Support</h3>
        <p>
          When disabled, the bot responds only in the selected language.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Show Floating Welcome Message</h3>
        <p>
          Toggle visibility of the welcome message that appears when a user first engages with the bot.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Welcome Message</h3>
        <p>
          Customize the message shown to users in different scenarios:
        </p>
        <ul>
          <li>Default greeting: <em>"Hey there, how can I help you?"</em></li>
          <li>No Source Added: <em>"The bot is yet to be trained..."</em></li>
          <li>Server Error: <em>"Apologies, there seems to be a server error."</em></li>
        </ul>
      </div>

      <h2 className="docs-bot-settings-section-title">Email & Support</h2>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Send Email Transcript</h3>
        <p>
          If enabled, users will receive their chat transcript via email once the conversation ends.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Main Customer Support Email</h3>
        <p>
          Set a general support email address for receiving queries and issues.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Add Support Emails</h3>
        <p>
          Automatically redirect specific issues to relevant email addresses.
        </p>
      </div>

      {/* Usage & Security Settings */}
      <h2 className="docs-bot-settings-section-title">Usage & Security</h2>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Usage Limit per User</h3>
        <p>
          Restrict the number of messages each user can send per day to prevent misuse. Applies to the widget and messaging apps like WhatsApp, Messenger, and Telegram.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">User Limit Warning</h3>
        <p>
          Set a custom message to display when users exceed their daily message limit.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Configure Bot Rate Limit</h3>
        <p>
          Set the API rate limit per minute. Maximum allowed is 300 requests per minute.
        </p>
      </div>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Access for Users with Masked IPs</h3>
        <p>
          Optionally block users with masked or anonymous IPs from accessing the chatbot.
        </p>
      </div>

      {/* Working Hours */}
      <h2 className="docs-bot-settings-section-title">Working Hours</h2>

      <div className="docs-bot-settings-option">
        <h3 className="docs-bot-settings-option-title">Enable Working Hours</h3>
        <p>
          Configure your bot's active hours to control when it can respond to user messages.
        </p>
      </div>

      <div className="docs-bot-settings-footer">
        <p>After configuring settings, you can:</p>
        <ul>
          <li><strong>Save Changes</strong> to apply updates.</li>
          <li><strong>Discard Changes</strong> to revert any unsaved modifications.</li>
        </ul>
      </div>
    </div>
  );
};

export default BotAdditionalSetting;
